import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { FileText, MapPin, Package, Truck, ArrowLeft, Info, Plane, Ship } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import PortSelector from '../components/PortSelector';
import api from '../utils/api';

const CreateRFQ = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    origin: '',
    destination: '',
    cargo_type: '',
    mode: '',
    weight: '',
    weight_unit: 'kg',
    dimensions: '',
    pieces: '',
    quantity: '',
    quantity_unit: 'units',
    incoterms: '',
    special_handling: '',
    is_public: true
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        weight: formData.weight ? parseFloat(formData.weight) : undefined,
        pieces: formData.pieces ? parseInt(formData.pieces) : undefined,
        quantity: formData.quantity ? parseFloat(formData.quantity) : undefined
      };

      await api.post('/rfqs', payload);
      toast.success('RFQ created successfully!');
      navigate('/rfqs');
    } catch (error) {
      console.error('RFQ creation error:', error.response?.data);
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Failed to create RFQ';
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail) && errorDetail.length > 0) {
        // Pydantic validation error format
        errorMessage = errorDetail.map(e => `${e.loc?.join('.')} - ${e.msg}`).join(', ');
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Popular Caribbean trade lanes for quick selection
  const popularRoutes = [
    { origin: 'MIA', destination: 'NAS', label: 'Miami → Nassau' },
    { origin: 'MIA', destination: 'KIN', label: 'Miami → Kingston' },
    { origin: 'MIA', destination: 'POS', label: 'Miami → Port of Spain' },
    { origin: 'SJU', destination: 'STT', label: 'San Juan → St. Thomas' },
  ];

  return (
    <>
      <Header />
      <div className="min-h-screen bg-slate-50 p-8">
        <div className="max-w-3xl mx-auto">
          <Button
            onClick={() => navigate('/rfqs')}
            variant="outline"
            className="mb-6"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Marketplace
          </Button>

          <div className="mb-8">
            <h1 className="text-4xl font-outfit font-bold text-ocean-900">Create New RFQ</h1>
            <p className="text-slate-600 mt-2">Post your freight requirements to receive competitive quotes from verified carriers</p>
          </div>

        {/* Quick Route Selection */}
        <Card className="mb-6 border-cyan-200 bg-cyan-50/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Info className="h-4 w-4 text-cyan-600" />
              <span className="text-sm font-medium text-cyan-800">Popular Caribbean Routes</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {popularRoutes.map((route, idx) => (
                <Button
                  key={idx}
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={() => setFormData({ ...formData, origin: route.origin, destination: route.destination })}
                  className="text-xs hover:bg-cyan-100 hover:border-cyan-300"
                >
                  {route.label}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-cyan-500" />
              Request for Quote Details
            </CardTitle>
            <CardDescription>
              Fields marked with * are required
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="title">RFQ Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Urgent electronics shipment Miami to Nassau"
                  required
                  data-testid="rfq-title-input"
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">Be descriptive to attract the right carriers</p>
              </div>

              <div className="bg-slate-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="h-4 w-4 text-cyan-500" />
                  <span className="font-medium text-sm">Route Information</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <PortSelector
                    id="origin"
                    label="Origin Port/City"
                    value={formData.origin}
                    onChange={(value) => setFormData({ ...formData, origin: value })}
                    placeholder="Search origin port..."
                    required
                  />
                  <PortSelector
                    id="destination"
                    label="Destination Port/City"
                    value={formData.destination}
                    onChange={(value) => setFormData({ ...formData, destination: value })}
                    placeholder="Search destination port..."
                    required
                  />
                </div>
                <p className="text-xs text-slate-500 mt-3">
                  Search by port code (e.g., MIA, NAS) or name. Browse by region to explore all available ports.
                </p>
              </div>

              <div className="bg-slate-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Truck className="h-4 w-4 text-cyan-500" />
                  <span className="font-medium text-sm">Transport Details</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="cargo_type">Cargo Type *</Label>
                    <Select value={formData.cargo_type} onValueChange={(v) => setFormData({ ...formData, cargo_type: v })} required>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {/* Pallet Options */}
                        <SelectItem value="pallet_6ft">Pallet (Up to 6ft)</SelectItem>
                        <SelectItem value="half_pallet">Half Pallet</SelectItem>
                        <SelectItem value="full_pallet">Full Pallet (Standard 48x40)</SelectItem>
                        <SelectItem value="euro_pallet">Euro Pallet (48x32)</SelectItem>
                        <SelectItem value="oversized_pallet">Oversized Pallet</SelectItem>
                        {/* Container Options */}
                        <SelectItem value="fcl_20">FCL 20ft Container</SelectItem>
                        <SelectItem value="fcl_40">FCL 40ft Container</SelectItem>
                        <SelectItem value="fcl_40hc">FCL 40ft High Cube</SelectItem>
                        <SelectItem value="lcl">LCL (Less Container Load)</SelectItem>
                        {/* Package Types */}
                        <SelectItem value="parcel">Parcel/Small Package</SelectItem>
                        <SelectItem value="box">Box/Carton</SelectItem>
                        <SelectItem value="crate">Crate</SelectItem>
                        <SelectItem value="drum">Drum/Barrel</SelectItem>
                        <SelectItem value="bags">Bags/Sacks</SelectItem>
                        <SelectItem value="bundle">Bundle</SelectItem>
                        {/* Weight-Based */}
                        <SelectItem value="per_kg">Per Kilogram</SelectItem>
                        <SelectItem value="per_lb">Per Pound</SelectItem>
                        <SelectItem value="per_cbm">Per Cubic Meter</SelectItem>
                        <SelectItem value="per_cft">Per Cubic Foot</SelectItem>
                        {/* Special Cargo */}
                        <SelectItem value="air_cargo">Air Cargo</SelectItem>
                        <SelectItem value="bulk">Bulk Cargo</SelectItem>
                        <SelectItem value="breakbulk">Breakbulk</SelectItem>
                        <SelectItem value="roro">RoRo (Roll-on/Roll-off)</SelectItem>
                        <SelectItem value="vehicle">Vehicle</SelectItem>
                        <SelectItem value="heavy_equipment">Heavy Equipment</SelectItem>
                        {/* Temperature Controlled */}
                        <SelectItem value="reefer">Reefer (Refrigerated)</SelectItem>
                        <SelectItem value="frozen">Frozen Goods</SelectItem>
                        <SelectItem value="chilled">Chilled Goods</SelectItem>
                        {/* Specialty */}
                        <SelectItem value="hazmat">Hazmat/Dangerous Goods</SelectItem>
                        <SelectItem value="perishable">Perishable</SelectItem>
                        <SelectItem value="fragile">Fragile</SelectItem>
                        <SelectItem value="livestock">Livestock</SelectItem>
                        <SelectItem value="general">General Cargo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="mode">Transport Mode *</Label>
                    <Select value={formData.mode} onValueChange={(v) => setFormData({ ...formData, mode: v })} required>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select mode" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="air">Air Freight</SelectItem>
                        <SelectItem value="sea">Ocean / Sea Freight</SelectItem>
                        <SelectItem value="land">Land / Trucking</SelectItem>
                        <SelectItem value="multimodal">Multimodal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Package className="h-4 w-4 text-cyan-500" />
                  <span className="font-medium text-sm">Cargo Specifications</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <Label htmlFor="weight">Weight</Label>
                    <Input
                      id="weight"
                      type="number"
                      step="0.01"
                      value={formData.weight}
                      onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                      className="mt-1"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <Label htmlFor="weight_unit">Unit</Label>
                    <Select value={formData.weight_unit} onValueChange={(v) => setFormData({ ...formData, weight_unit: v })}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="kg">Kilograms (kg)</SelectItem>
                        <SelectItem value="mt">Metric Tons (MT)</SelectItem>
                        <SelectItem value="lbs">Pounds (lbs)</SelectItem>
                        <SelectItem value="cbm">Cubic Meters (CBM)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="pieces">Number of Pieces</Label>
                    <Input
                      id="pieces"
                      type="number"
                      value={formData.pieces}
                      onChange={(e) => setFormData({ ...formData, pieces: e.target.value })}
                      className="mt-1"
                      placeholder="1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="quantity">Quantity (for pricing)</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        id="quantity"
                        type="number"
                        step="0.01"
                        value={formData.quantity}
                        onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                        placeholder="1"
                        className="flex-1"
                      />
                      <Select value={formData.quantity_unit} onValueChange={(v) => setFormData({ ...formData, quantity_unit: v })}>
                        <SelectTrigger className="w-24">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="units">Units</SelectItem>
                          <SelectItem value="pallets">Pallets</SelectItem>
                          <SelectItem value="containers">Containers</SelectItem>
                          <SelectItem value="cbm">CBM</SelectItem>
                          <SelectItem value="kg">KG</SelectItem>
                          <SelectItem value="mt">Metric Tons (MT)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="dimensions">Dimensions (L x W x H)</Label>
                    <Input
                      id="dimensions"
                      value={formData.dimensions}
                      onChange={(e) => setFormData({ ...formData, dimensions: e.target.value })}
                      placeholder="e.g., 120 x 80 x 100 cm"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="incoterms">INCOTERMS</Label>
                    <Select value={formData.incoterms} onValueChange={(v) => setFormData({ ...formData, incoterms: v })}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select terms" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="EXW">EXW - Ex Works</SelectItem>
                        <SelectItem value="FOB">FOB - Free on Board</SelectItem>
                        <SelectItem value="CIF">CIF - Cost, Insurance & Freight</SelectItem>
                        <SelectItem value="CFR">CFR - Cost and Freight</SelectItem>
                        <SelectItem value="DDP">DDP - Delivered Duty Paid</SelectItem>
                        <SelectItem value="DAP">DAP - Delivered at Place</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="special_handling">Special Handling Requirements</Label>
                <Textarea
                  id="special_handling"
                  value={formData.special_handling}
                  onChange={(e) => setFormData({ ...formData, special_handling: e.target.value })}
                  placeholder="Temperature control, hazmat classification, fragile items, time-sensitive delivery, customs requirements..."
                  rows={3}
                  className="mt-1"
                />
              </div>

              <div className="flex items-center space-x-3 p-4 bg-slate-50 rounded-lg">
                <input
                  type="checkbox"
                  id="is_public"
                  checked={formData.is_public}
                  onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
                  className="rounded border-gray-300 h-5 w-5 text-cyan-500 focus:ring-cyan-500"
                />
                <div>
                  <Label htmlFor="is_public" className="cursor-pointer font-medium">
                    Post to Public Marketplace
                  </Label>
                  <p className="text-xs text-slate-500">Visible to all verified network members</p>
                </div>
              </div>

              <div className="flex gap-4 pt-6 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/rfqs')}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading || !formData.title || !formData.origin || !formData.destination || !formData.cargo_type || !formData.mode}
                  className="flex-1 bg-cyan-500 hover:bg-cyan-600"
                  data-testid="rfq-submit-button"
                >
                  {loading ? 'Creating...' : 'Post RFQ to Marketplace'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
    </>
  );
};

export default CreateRFQ;
